import React, { useState, useEffect } from "react";
import { useBranch } from '../../../layouts/BranchContext'; 
import {
  Button,
  TextField,
  Tooltip,
  Container,
  styled,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Radio,
  RadioGroup,
  FormControlLabel,
  useTheme,
  CircularProgress,
} from "@mui/material";

import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Download as DownloadIcon,
  Upload as UploadIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";

import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { usePdfSettings } from "../../../layouts/PdfSettingsContext.js";

/* =========================================================
   DYNAMIC TABLE SCROLLBAR
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  width: "100%",
  maxWidth: "100%",
  maxHeight: "calc(100vh - 350px)",
  overflow: "auto",
  boxSizing: "border-box",

  scrollbarWidth: "thin",
  scrollbarColor: `${theme.palette.primary.main} transparent`,

  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
    borderRadius: "10px",
  },

  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "10px",
  },

  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&::-webkit-scrollbar-corner": {
    backgroundColor: "transparent",
  },
}));

/* =========================================================
   ALERT
========================================================= */

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

/* =========================================================
   GET ABSOLUTE IMAGE URL
========================================================= */

const getAbsoluteImageUrl = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }

  const image = value.trim();

  if (!image) {
    return "";
  }

  if (
    image.startsWith("data:image/") ||
    image.startsWith("blob:") ||
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  if (image.startsWith("//")) {
    return `https:${image}`;
  }

  const apiOrigin = (
    process.env.REACT_APP_API_BASE_URL ||
    "http://localhost:8000"
  ).replace(/\/api\/?$/, "");

  if (image.startsWith("/")) {
    return `${apiOrigin}${image}`;
  }

  return `${apiOrigin}/${image}`;
};

/* =========================================================
   IMAGE URL -> DATA URL
========================================================= */

const imageUrlToDataUrl = async (imageUrl) => {
  try {
    if (!imageUrl) {
      return null;
    }

    if (imageUrl.startsWith("data:image/")) {
      return imageUrl;
    }

    const response = await fetch(imageUrl);

    if (!response.ok) {
      throw new Error(
        `Failed to load image: ${response.status}`
      );
    }

    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;

      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn(
      "Unable to convert logo to data URL:",
      error
    );

    return null;
  }
};

/* =========================================================
   HEX TO RGB
========================================================= */

const hexToRgb = (hex) => {
  const cleanHex = String(
    hex || "#000000"
  ).replace("#", "");

  const normalized =
    cleanHex.length === 3
      ? cleanHex
          .split("")
          .map((char) => char + char)
          .join("")
      : cleanHex;

  return [
    parseInt(
      normalized.substring(0, 2),
      16
    ),
    parseInt(
      normalized.substring(2, 4),
      16
    ),
    parseInt(
      normalized.substring(4, 6),
      16
    ),
  ];
};

/* =========================================================
   REGISTRATION PDF
========================================================= */

const generateRegistrationPDF = async (
  registration,
  pdfColors = {},
  branchLogoUrl = "",
  selectedBranchName = ""
) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;

  const primary = hexToRgb(pdfColors.primary || "#2457A7");
  const accent = hexToRgb(pdfColors.accent || "#EAF1FC");
  const textColor = hexToRgb(pdfColors.text || "#243247");
  const muted = [100, 116, 139];
  const lineColor = [203, 213, 225];
  const white = [255, 255, 255];

  const safe = (value) =>
    value !== null && value !== undefined && String(value).trim() !== ""
      ? String(value).trim()
      : "—";

  let sessionUser = {};
  try {
    sessionUser = JSON.parse(sessionStorage.getItem("user")) || {};
  } catch (error) {
    console.warn("Unable to parse session user:", error);
  }

  const organizationName =
    registration?.branchDetails?.organization_name ||
    registration?.branchDetails?.organizationName ||
    registration?.organizationName ||
    registration?.organization_name ||
    registration?.organization?.name ||
    registration?.organization?.organization_name ||
    registration?.institutionName ||
    registration?.institution_name ||
    sessionUser?.organization_name ||
    "Warals Technology";

  const branchName =
    selectedBranchName ||
    registration?.branchName ||
    registration?.branch_name ||
    "";

  const registrationDate = registration?.registrationDate
    ? new Date(registration.registrationDate).toLocaleDateString("en-GB")
    : new Date().toLocaleDateString("en-GB");

  let y = 12;

  const drawPageFrame = () => {
    doc.setDrawColor(...lineColor);
    doc.setLineWidth(0.35);
    doc.rect(8, 8, pageWidth - 16, pageHeight - 16);
    doc.setFillColor(...primary);
    doc.rect(8, 8, pageWidth - 16, 2, "F");
  };

  const ensureSpace = (height) => {
    if (y + height > pageHeight - 22) {
      doc.addPage();
      drawPageFrame();
      y = 18;
    }
  };

  const drawSection = (title) => {
    ensureSpace(10);
    doc.setFillColor(...accent);
    doc.roundedRect(margin, y, contentWidth, 6.5, 1.2, 1.2, "F");
    doc.setFillColor(...primary);
    doc.roundedRect(margin, y, 2, 6.5, 0.8, 0.8, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...primary);
    doc.text(title.toUpperCase(), margin + 5, y + 4.4);
    y += 9;
  };

  const drawField = (x, fieldWidth, label, value, options = {}) => {
    const labelY = y;
    const valueText = safe(value);
    const labelSize = 7;
    const valueSize = 8.2;
    const textX = x + 1;
    const valueY = labelY + 5.8;
    const lines = doc.splitTextToSize(valueText, fieldWidth - 4);
    const valueHeight = Math.max(3.5, lines.length * 3.4);
    const rowHeight = Math.max(options.minHeight || 11, 7.5 + valueHeight);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(labelSize);
    doc.setTextColor(...muted);
    doc.text(label, textX, labelY);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(valueSize);
    doc.setTextColor(...textColor);
    doc.text(lines, textX, valueY);

    doc.setDrawColor(...lineColor);
    doc.setLineWidth(0.25);
    doc.line(x, labelY + rowHeight - 1, x + fieldWidth, labelY + rowHeight - 1);
    return rowHeight;
  };

  const drawRow = (fields, gap = 8) => {
    const count = fields.length;
    const fieldWidth = (contentWidth - gap * (count - 1)) / count;
    const rowStartY = y;
    let maxHeight = 15;

    fields.forEach((item, index) => {
      const x = margin + index * (fieldWidth + gap);
      const h = drawField(x, fieldWidth, item.label, item.value, item.options || {});
      maxHeight = Math.max(maxHeight, h);
    });

    y = rowStartY + maxHeight + 1.5;
  };

  drawPageFrame();

  // Header: clean stationery-style layout, inspired by the admission form.
  let logoLoaded = false;
  try {
    if (branchLogoUrl) {
      const logoDataUrl = await imageUrlToDataUrl(branchLogoUrl);
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "PNG", margin, y + 1, 23, 20);
        logoLoaded = true;
      }
    }
  } catch (error) {
    console.warn("Unable to add branch logo to PDF:", error);
  }

  // Keep organization and branch centered on the full page,
  // matching the dynamic header layout used by Admission.js.
  // The logo stays on the left without shifting the heading.
  const headerTextX = pageWidth / 2;
  const headerAlign = "center";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...primary);
  doc.text(safe(organizationName), headerTextX, y + 6, { align: headerAlign });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...muted);
  if (branchName) {
    doc.text(safe(branchName), headerTextX, y + 11, { align: headerAlign });
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...textColor);
  doc.text("REGISTRATION FORM", pageWidth / 2, y + 20, { align: "center" });

  // Passport photo placeholder, matching the printed admission form format.
  const photoX = pageWidth - margin - 23;
  doc.setDrawColor(...lineColor);
  doc.setLineWidth(0.4);
  doc.rect(photoX, y, 23, 27);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...muted);
  doc.text(["PASSPORT", "SIZE PHOTO", "(Paste here)"], photoX + 11.5, y + 4, {
    align: "center",
  });

  y += 27;
  doc.setDrawColor(...primary);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...muted);
  doc.text("Please verify the information below. Required details are marked on the registration record.", margin, y);
  y += 5;

  // Registration reference strip.
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, contentWidth, 10, 1.3, 1.3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...primary);
  doc.text("REGISTRATION NO.", margin + 4, y + 3.8);
  doc.setFontSize(10);
  doc.text(safe(registration?.registrationCode), margin + 4, y + 8);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text("REGISTRATION DATE", pageWidth / 2 + 4, y + 3.8);
  doc.setFontSize(10);
  doc.text(registrationDate, pageWidth / 2 + 4, y + 8);
  y += 13;

  drawSection("Applicant Details");
  drawRow([
    { label: "Full Name", value: registration?.candidateName },
    { label: "Date of Birth", value: registration?.dateOfBirth },
  ]);
  drawRow([
    { label: "Mobile Number", value: registration?.mobileNo },
    { label: "Email Address", value: registration?.email },
  ]);
  const nationalityValue = registration?.nationality;
  const nationalityDisplay =
    typeof nationalityValue === "number" ||
    (typeof nationalityValue === "string" && /^\d+$/.test(nationalityValue.trim()))
      ? registration?.nationalityName || registration?.nationality_name || "—"
      : nationalityValue;

  drawRow([
    { label: "Gender", value: registration?.gender },
    { label: "Nationality", value: nationalityDisplay },
  ]);
  drawRow([
    { label: "Place of Birth", value: registration?.placeOfBirth },
    { label: "Religion", value: registration?.religion },
  ]);
  drawRow([
    { label: "Residence Status", value: registration?.residenceStatus },
    { label: "Marital Status", value: registration?.maritalStatus },
  ]);
  drawRow([{ label: "Full Address", value: registration?.address, options: { minHeight: 13 } }]);

  drawSection("Parent / Guardian Details");
  drawRow([
    { label: "Father's / Guardian's Name", value: registration?.fatherName },
    { label: "Mother's Name", value: registration?.motherName },
  ]);

  drawSection("Course Details");
  drawRow([
    { label: "Course / Programme", value: registration?.course },
    { label: "Payment Method", value: registration?.paymentMethod },
  ]);
  drawRow([
    { label: "Registration Fee", value: registration?.registrationFee != null ? `₹ ${registration.registrationFee}` : "—" },
    { label: "Remarks / Comments", value: registration?.comments },
  ]);

  drawSection("Declaration & Office Use");
  ensureSpace(20);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...textColor);
  const declaration = "I declare that the information provided above is true and complete to the best of my knowledge.";
  const declarationLines = doc.splitTextToSize(declaration, contentWidth);
  doc.text(declarationLines, margin, y);
  y += Math.max(6, declarationLines.length * 3.6 + 2);

  const signatureGap = 10;
  const signatureWidth = (contentWidth - signatureGap) / 2;
  const drawSignatureLine = (x, label, width) => {
    doc.setDrawColor(...lineColor);
    doc.line(x, y + 6, x + width, y + 6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    doc.text(label, x, y + 9);
  };
  drawSignatureLine(margin, "Applicant Signature", signatureWidth);
  drawSignatureLine(margin + signatureWidth + signatureGap, "Date", signatureWidth);
  y += 14;
  drawSignatureLine(margin, "Admission No. / Code (Office Use)", signatureWidth);
  drawSignatureLine(margin + signatureWidth + signatureGap, "Verified By (Office Use)", signatureWidth);

  // Consistent footer on every page.
  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setDrawColor(...lineColor);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...muted);
    doc.text(`${safe(organizationName)} • Student Registration Form`, pageWidth / 2, pageHeight - 10, { align: "center" });
    doc.text(`Printed on ${new Date().toLocaleDateString("en-GB")} • Confidential • Page ${page} of ${pageCount}`, pageWidth / 2, pageHeight - 6, { align: "center" });
  }

  const blobUrl = doc.output("bloburl");
  window.open(blobUrl, "_blank");
};

/* =========================================================
   REGISTRATION COMPONENT
========================================================= */

const Registration = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pdfThemeColor, pdfTextColor } = usePdfSettings();

  const clientData = JSON.parse(
    localStorage.getItem(
      "client"
    ) || "null"
  );

  const token =
    localStorage.getItem(
      "accessToken"
    );

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    "http://localhost:8000";

  /* =====================================================
     SESSION USER / BRANCH ACCESS
  ====================================================== */

  const sessionUser = (() => {
    try {
      return (
        JSON.parse(
          sessionStorage.getItem("user") || "null"
        ) || {}
      );
    } catch (error) {
      console.warn(
        "Unable to parse session user:",
        error
      );
      return {};
    }
  })();

  const isMainBranchAdmin =
    sessionUser?.is_main_branch_admin === true;

  const isBranchAdmin =
    sessionUser?.is_admin === true ||
    sessionUser?.is_main_branch_admin === true ||
    sessionUser?.is_branch_super_admin === true;

      const { selectedBranchId: selectedBranch } = useBranch();

  /* =====================================================
     STATE
  ====================================================== */

  

  const [branchLogo, setBranchLogo] =
    useState("");

  const [branchName, setBranchName] =
    useState("");

  const [page, setPage] =
    useState(1);

  const [rowsPerPage, setRowsPerPage] =
    useState(5);

  const rowsPerPageOptions = [
    5,
    10,
    25,
    50,
  ];

  const [openSnackbar, setOpenSnackbar] =
    useState(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState("");

  const [snackbarSeverity, setSnackbarSeverity] =
    useState("success");

  const [searchQuery, setSearchQuery] =
    useState("");

  const [showDeleteModal, setShowDeleteModal] =
    useState(false);

  const [showUserGuideModal, setShowUserGuideModal] =
    useState(false);

  const [showSuccessModal, setShowSuccessModal] =
    useState(false);

  const [showShareModal, setShowShareModal] =
    useState(false);

  // Excel import/export state
  const [showImportModal, setShowImportModal] =
    useState(false);

  const [showExportModal, setShowExportModal] =
    useState(false);

  // "active" exports active records only; "all" includes inactive records too.
  const [exportType, setExportType] =
    useState("active");

  const [importFile, setImportFile] =
    useState(null);

  const [importing, setImporting] =
    useState(false);

  const [exporting, setExporting] =
    useState(false);

  const [downloadingTemplate, setDownloadingTemplate] =
    useState(false);

  /* =====================================================
     PDF COLOR PICKER STATE
  ====================================================== */

  const [showPdfColorModal, setShowPdfColorModal] =
    useState(false);

  const [pdfColorPrimary, setPdfColorPrimary] =
    useState(pdfThemeColor);

  const [pdfColorText, setPdfColorText] =
    useState(pdfTextColor);

  const [pdfColorAccent, setPdfColorAccent] =
    useState("#FFFFFF");

  const [selectedPdfRegistration, setSelectedPdfRegistration] =
    useState(null);

  const [pdfLoading, setPdfLoading] =
    useState(false);

  useEffect(() => {
    if (showPdfColorModal) {
      setPdfColorPrimary(pdfThemeColor);
      setPdfColorText(pdfTextColor);
    }
  }, [showPdfColorModal, pdfThemeColor, pdfTextColor]);

  const [shareableLink, setShareableLink] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [fetchError, setFetchError] =
    useState(null);

  const [registrationData, setRegistrationData] =
    useState([]);

  const [deleteLoading, setDeleteLoading] =
    useState(false);

  const [selectedRegistration, setSelectedRegistration] =
    useState({
      registrationId: null,
      candidateName: "",
    });

  const [filteredData, setFilteredData] =
    useState([]);

  /* =====================================================
     ORGANIZATION ID
  ====================================================== */

  const getOrganizationId = () => {
    let sessionUser = {};

    try {
      sessionUser =
        JSON.parse(
          sessionStorage.getItem(
            "user"
          )
        ) || {};
    } catch (error) {
      console.warn(
        "Unable to parse session user:",
        error
      );
    }

    return (
      sessionUser?.organization_id ||
      clientData?.id
    );
  };

  /* =====================================================
     FETCH BRANCH DETAILS
  ====================================================== */

  const fetchBranchDetails = async (
    branchId
  ) => {
    if (!branchId) {
      setBranchLogo("");
      setBranchName("");
      return null;
    }

    try {
      const response =
        await fetch(
          `${API_BASE_URL}/api/master/branch/${branchId}/`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`,
              "Content-Type":
                "application/json",
            },
          }
        );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch branch details: ${response.status}`
        );
      }

      const data =
        await response.json();

      console.log(
        "SELECTED BRANCH DETAILS:",
        data
      );

      const name =
        data?.name ||
        data?.branch_name ||
        data?.branchName ||
        "";

      const image =
        data?.image ||
        data?.logo ||
        data?.branch_logo ||
        data?.branchLogo ||
        "";

      const imageUrl =
        getAbsoluteImageUrl(
          image
        );

      setBranchName(name);
      setBranchLogo(imageUrl);

      return {
        name,
        logo: imageUrl,
        data,
      };
    } catch (error) {
      console.error(
        "Branch details error:",
        error
      );

      setBranchLogo("");
      setBranchName("");

      return null;
    }
  };

  /* =====================================================
     FETCH REGISTRATIONS
  ====================================================== */

  useEffect(() => {
    const fetchRegistrations =
      async () => {
        if (!token) {
          setFetchError(
            "No authentication token found"
          );

          setLoading(false);
          return;
        }

        setLoading(true);
        setFetchError(null);

        try {
          const params =
            new URLSearchParams();

          const organizationId =
            getOrganizationId();

          if (organizationId) {
            params.append(
              "organization",
              organizationId
            );
          }

          if (
            selectedBranch &&
            selectedBranch !== "all"
          ) {
            params.append(
              "branch",
              selectedBranch
            );
          }

          const queryString =
            params.toString();

          const requestUrl =
            `${API_BASE_URL}/api/student_details/registrations/` +
            (queryString
              ? `?${queryString}`
              : "");

          const res =
            await fetch(
              requestUrl,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`,
                  "Content-Type":
                    "application/json",
                },
              }
            );

          if (!res.ok) {
            throw new Error(
              `Failed to fetch registrations: ${res.status}`
            );
          }

          const data =
            await res.json();

          const registrationsArray =
            Array.isArray(data)
              ? data
              : data?.results ||
                data?.data ||
                data?.registrations ||
                [];

          const branchFilteredArray =
            selectedBranch &&
            selectedBranch !== "all"
              ? registrationsArray.filter(
                  (item) => {
                    const itemBranchId =
                      item?.branch?.id ??
                      item?.branch_id ??
                      item?.branch;

                    return (
                      String(
                        itemBranchId
                      ) ===
                      String(
                        selectedBranch
                      )
                    );
                  }
                )
              : registrationsArray;

          const formatted =
            branchFilteredArray.map(
              (item) => ({
                registrationId:
                  item.id ??
                  item.registration_id ??
                  item.pk ??
                  null,

                registrationCode:
                  item.registration_code ||
                  item.code ||
                  "",

                registrationDate:
                  item.registration_date ||
                  item.date ||
                  "",

                candidateName:
                  item.candidate_name ||
                  item.student_name ||
                  item.name ||
                  "",

                gender:
                  item.gender ||
                  "",

                mobileNo:
                  item.mobile_no ||
                  item.mobile ||
                  item.phone ||
                  "",

                email:
                  item.email ||
                  item.student_email ||
                  "",

                dateOfBirth:
                  item.dob ||
                  item.date_of_birth ||
                  "",

                country:
                  item.country_name ||
                  item.country ||
                  "",

                state:
                  item.state_name ||
                  item.state ||
                  "",

                city:
                  item.city_name ||
                  item.city ||
                  "",

                address:
                  item.address ||
                  "",

                nationality:
                  item.nationality_name ||
                  item.nationality ||
                  "",

                registrationFee:
                  item.registration_fee ??
                  item.fee ??
                  "",

                course:
                  item.course_name ||
                  item.course ||
                  "",

                paymentMethod:
                  item.payment_method ||
                  item.paymentMethod ||
                  item.payment_mode ||
                  "",

                fatherName:
                  item.father_name ||
                  item.fatherName ||
                  "",

                motherName:
                  item.mother_name ||
                  item.motherName ||
                  "",

                placeOfBirth:
                  item.place_of_birth ||
                  item.placeOfBirth ||
                  "",

                religion:
                  item.religion_name ||
                  item.religion ||
                  "",

                residenceStatus:
                  item.residence_status ||
                  item.residenceStatus ||
                  "",

                maritalStatus:
                  item.marital_status ||
                  item.maritalStatus ||
                  "",

                zipCode:
                  item.zip_code ||
                  item.zipCode ||
                  item.postal_code ||
                  "",

                comments:
                  item.comments ||
                  item.remark ||
                  item.remarks ||
                  "",

                sendSMS:
                  item.send_sms ??
                  item.sendSMS ??
                  false,

                branchId:
                  item?.branch?.id ??
                  item?.branch_id ??
                  item?.branch ??
                  null,
              })
            );

          setRegistrationData(
            formatted
          );

          setFilteredData(
            formatted
          );

          setSelectedRegistration({
            registrationId: null,
            candidateName: "",
          });

          setPage(1);
        } catch (err) {
          console.error(
            "Registration fetch error:",
            err
          );

          setFetchError(
            err.message ||
              "Failed to load registrations"
          );
        } finally {
          setLoading(false);
        }
      };

    fetchRegistrations();
  }, [selectedBranch]);

  /* =====================================================
     LOAD SELECTED BRANCH LOGO
  ====================================================== */

  useEffect(() => {
    if (
      selectedBranch &&
      selectedBranch !== "all"
    ) {
      fetchBranchDetails(
        selectedBranch
      );
    } else {
      setBranchLogo("");
      setBranchName("");
    }
  }, [selectedBranch]);

  /* =====================================================
     SEARCH
  ====================================================== */

  useEffect(() => {
    const query =
      searchQuery
        .trim()
        .toLowerCase();

    const filtered =
      registrationData.filter(
        (registration) =>
          String(
            registration.candidateName ||
              ""
          )
            .toLowerCase()
            .includes(query)
      );

    setFilteredData(
      filtered
    );

    setPage(1);
  }, [
    searchQuery,
    registrationData,
  ]);

  /* =====================================================
     GENERATE SHARE LINK
  ====================================================== */

   const generateShareableLink = (
    registrationId
  ) => {
    const baseUrl =
      window.location.origin;

    const uniqueToken =
      uuidv4();

    let tokens = {};

    const storedTokens =
      localStorage.getItem(
        "registrationTokens"
      );

    if (
      storedTokens &&
      storedTokens !==
        "undefined"
    ) {
      try {
        tokens =
          JSON.parse(
            storedTokens
          );
      } catch (err) {
        console.warn(
          "Invalid registrationTokens in localStorage:",
          err
        );
      }
    }

       tokens[uniqueToken] = {
      registrationId: registrationId || null,
      used: false,
    };

    localStorage.setItem(
      "registrationTokens",
      JSON.stringify(tokens)
    );

    return registrationId
      ? `${baseUrl}/share-registration?regId=${registrationId}&token=${uniqueToken}`
      : `${baseUrl}/share-registration?token=${uniqueToken}`;
  };

  /* =====================================================
     SHARE REGISTRATION
  ====================================================== */

  const handleShare = (
    registration
  ) => {
    if (
      !registration ||
      registration.registrationId ===
        null ||
      registration.registrationId ===
        undefined ||
      registration.registrationId ===
        ""
    ) {
      setSnackbarMessage(
        "Please select a registration before sharing the link."
      );

      setSnackbarSeverity(
        "warning"
      );

      setOpenSnackbar(
        true
      );

      return;
    }

    setSelectedRegistration({
      registrationId:
        registration.registrationId,

      candidateName:
        registration.candidateName ||
        "",
    });

    const link =
      generateShareableLink(
        registration.registrationId
      );

    if (!link) {
      setSnackbarMessage(
        "Unable to generate registration link."
      );

      setSnackbarSeverity(
        "error"
      );

      setOpenSnackbar(
        true
      );

      return;
    }

    setShareableLink(
      link
    );

    setShowShareModal(
      true
    );
  };

  /* =====================================================
     SELECT REGISTRATION
  ====================================================== */

  const handleSelectRegistration = (
    registration
  ) => {
    if (
      !registration ||
      registration.registrationId ===
        null ||
      registration.registrationId ===
        undefined ||
      registration.registrationId ===
        ""
    ) {
      return;
    }

    setSelectedRegistration({
      registrationId:
        registration.registrationId,

      candidateName:
        registration.candidateName ||
        "",
    });
  };

  /* =====================================================
     TOP SHARE
  ====================================================== */

   const handleTopShare = () => {
    const link = generateShareableLink(
      selectedRegistration.registrationId || null
    );

    setShareableLink(link);
    setShowShareModal(true);
  };

  /* =====================================================
     COPY LINK
  ====================================================== */

  const handleCopyLink =
    async () => {
      if (!shareableLink) {
        return;
      }

      try {
        await navigator.clipboard.writeText(
          shareableLink
        );

        setSnackbarMessage(
          "Registration link copied successfully!"
        );

        setSnackbarSeverity(
          "success"
        );

        setOpenSnackbar(
          true
        );
      } catch (error) {
        console.error(
          "Clipboard copy failed:",
          error
        );

        const textarea =
          document.createElement(
            "textarea"
          );

        textarea.value =
          shareableLink;

        textarea.style.position =
          "fixed";

        textarea.style.opacity =
          "0";

        document.body.appendChild(
          textarea
        );

        textarea.focus();
        textarea.select();

        try {
          document.execCommand(
            "copy"
          );

          setSnackbarMessage(
            "Registration link copied successfully!"
          );

          setSnackbarSeverity(
            "success"
          );

          setOpenSnackbar(
            true
          );
        } catch (err) {
          console.error(
            "Fallback copy failed:",
            err
          );

          setSnackbarMessage(
            "Unable to copy link"
          );

          setSnackbarSeverity(
            "error"
          );

          setOpenSnackbar(
            true
          );
        }

        document.body.removeChild(
          textarea
        );
      }
    };

  /* =====================================================
     EDIT
  ====================================================== */

  const handleEdit = (
    registration
  ) => {
    setSelectedRegistration({
      registrationId:
        registration.registrationId,

      candidateName:
        registration.candidateName ||
        "",
    });

    navigate(
      `/edit-registration/${registration.registrationId}`
    );
  };

  /* =====================================================
     VIEW / PRINT
  ====================================================== */

  const handleView = (
    registration
  ) => {
    setSelectedPdfRegistration(
      registration
    );

    setShowPdfColorModal(
      true
    );
  };

  /* =====================================================
     GENERATE PDF
  ====================================================== */

  const handleGeneratePDF =
    async () => {
      if (
        !selectedPdfRegistration
      ) {
        return;
      }

      setPdfLoading(true);

      try {
        let currentLogo =
          branchLogo;

        let currentBranchName =
          branchName;

        /*
         * If logo/name has not loaded yet,
         * fetch it before generating PDF.
         */

        if (
          selectedBranch &&
          selectedBranch !== "all" &&
          (!currentLogo ||
            !currentBranchName)
        ) {
          const branchDetails =
            await fetchBranchDetails(
              selectedBranch
            );

          currentLogo =
            branchDetails?.logo ||
            currentLogo;

          currentBranchName =
            branchDetails?.name ||
            currentBranchName;
        }

        await generateRegistrationPDF(
          selectedPdfRegistration,
          {
            primary:
              pdfColorPrimary,

            text:
              pdfColorText,

            accent:
              pdfColorAccent,
          },
          currentLogo,
          currentBranchName
        );

        setShowPdfColorModal(
          false
        );
      } catch (error) {
        console.error(
          "PDF generation error:",
          error
        );

        setSnackbarMessage(
          "Unable to generate PDF."
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(
          true
        );
      } finally {
        setPdfLoading(
          false
        );
      }
    };

  /* =====================================================
     RESET PDF COLORS
  ====================================================== */

  const handleResetPdfColors =
    () => {
      setPdfColorPrimary(
        pdfThemeColor
      );

      setPdfColorText(
        pdfTextColor
      );

      setPdfColorAccent(
        "#FFFFFF"
      );
    };

  /* =====================================================
     DELETE MODAL
  ====================================================== */

  const handleShowDeleteModal = (
    registrationId,
    candidateName
  ) => {
    setSelectedRegistration({
      registrationId,
      candidateName,
    });

    setShowDeleteModal(
      true
    );
  };

  /* =====================================================
     DELETE
  ====================================================== */

  const handleDelete =
    async () => {
      if (!token) {
        setSnackbarMessage(
          "Authentication token ming"
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(
          true
        );

        setShowDeleteModal(
          false
        );

        return;
      }

      if (
        selectedRegistration.registrationId ===
          null ||
        selectedRegistration.registrationId ===
          undefined ||
        selectedRegistration.registrationId ===
          ""
      ) {
        setSnackbarMessage(
          "Invalid registration selected."
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(
          true
        );

        setShowDeleteModal(
          false
        );

        return;
      }

      setDeleteLoading(
        true
      );

      try {
        const headers = {
          Authorization:
            `Bearer ${token}`,

          "Content-Type":
            "application/json",
        };

        const response =
          await fetch(
            `${API_BASE_URL}/api/student_details/registrations/${selectedRegistration.registrationId}/`,
            {
              method: "DELETE",
              headers,
            }
          );

        if (!response.ok) {
          const errorData =
            await response
              .json()
              .catch(
                () => ({})
              );

          throw new Error(
            errorData.detail ||
              errorData.message ||
              `HTTP ${response.status}`
          );
        }

        const deletedId =
          selectedRegistration.registrationId;

        const deletedName =
          selectedRegistration.candidateName;

        setRegistrationData(
          (prev) =>
            prev.filter(
              (reg) =>
                reg.registrationId !==
                deletedId
            )
        );

        setSelectedRegistration({
          registrationId: null,
          candidateName: "",
        });

        setSnackbarMessage(
          `${deletedName} registration deleted successfully!`
        );

        setSnackbarSeverity(
          "success"
        );

        setOpenSnackbar(
          true
        );
      } catch (err) {
        console.error(
          "Delete error:",
          err
        );

        setSnackbarMessage(
          err.message ||
            "Failed to delete registration"
        );

        setSnackbarSeverity(
          "error"
        );

        setOpenSnackbar(
          true
        );
      } finally {
        setDeleteLoading(
          false
        );

        setShowDeleteModal(
          false
        );
      }
    };

  /* =====================================================
     REGISTRATION EXCEL IMPORT / EXPORT
  ====================================================== */

  const downloadExcelBlob = (data, fileName) => {
    const blob =
      data instanceof Blob
        ? data
        : new Blob([data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadRegistrationTemplate = async () => {
    if (!token) {
      setSnackbarMessage("Please log in again to download the template.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    try {
      setDownloadingTemplate(true);

      const response = await fetch(
        `${API_BASE_URL}/api/student_details/registrations/import-template/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData?.detail ||
            errorData?.message ||
            `Template download failed (${response.status})`
        );
      }

      const blob = await response.blob();
      downloadExcelBlob(blob, "registration_import_template.xlsx");

      setSnackbarMessage("Template downloaded successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Registration template download failed:", error);
      setSnackbarMessage(error.message || "Failed to download template");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleRegistrationFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      setSnackbarMessage("Please select an Excel file (.xlsx or .xls)");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      event.target.value = "";
      return;
    }

    setImportFile(file);
  };

  const handleRegistrationImport = async () => {
    if (!importFile) {
      setSnackbarMessage("Please select an Excel file to import.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    if (!token) {
      setSnackbarMessage("Please log in again to import registrations.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    try {
      setImporting(true);

      const formData = new FormData();
      formData.append("file", importFile);

      const response = await fetch(
        `${API_BASE_URL}/api/student_details/registrations/import/excel/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          responseData?.message ||
            responseData?.error ||
            responseData?.detail ||
            `Import failed (${response.status})`
        );
      }

      setSnackbarMessage(
        responseData?.message || "Registrations imported successfully."
      );
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setShowImportModal(false);
      setImportFile(null);

      // Refresh the list using the current organization and branch context.
      window.location.reload();
    } catch (error) {
      console.error("Registration import failed:", error);
      setSnackbarMessage(error.message || "Failed to import registrations.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setImporting(false);
    }
  };

  const handleRegistrationExport = async () => {
    if (!token) {
      setSnackbarMessage("Please log in again to export registrations.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    try {
      setExporting(true);

      const params = new URLSearchParams();
      const organizationId = getOrganizationId();

      if (organizationId) {
        params.set("organization", organizationId);
      }

      if (selectedBranch && selectedBranch !== "all") {
        params.set("branch", selectedBranch);
      }

      if (searchQuery.trim()) {
        params.set("search", searchQuery.trim());
      }

      // API defaults to active records. Set true only when inactive records
      // should also be included.
      if (exportType === "all") {
        params.set("include_inactive", "true");
      }

      const queryString = params.toString();
      const url =
        `${API_BASE_URL}/api/student_details/registrations/export/excel/` +
        (queryString ? `?${queryString}` : "");

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData?.detail ||
            errorData?.message ||
            `Export failed (${response.status})`
        );
      }

      const blob = await response.blob();
      downloadExcelBlob(
        blob,
        exportType === "active"
          ? "active_registrations.xlsx"
          : "all_registrations.xlsx"
      );

      setSnackbarMessage("Registrations exported successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setShowExportModal(false);
    } catch (error) {
      console.error("Registration export failed:", error);
      setSnackbarMessage(error.message || "Failed to export registrations.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setExporting(false);
    }
  };

  /* =====================================================
     PAGINATION
  ====================================================== */

  const handleChangePage = (
    event,
    newPage
  ) => {
    setPage(
      newPage
    );
  };

  const handleChangeRowsPerPage = (
    event
  ) => {
    setRowsPerPage(
      parseInt(
        event.target.value,
        10
      )
    );

    setPage(1);
  };

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        filteredData.length /
          rowsPerPage
      )
    );

  const paginatedData =
    filteredData.slice(
      (page - 1) *
        rowsPerPage,
      page *
        rowsPerPage
    );

  /* =====================================================
     RENDER
  ====================================================== */

  return (
    <Container
      maxWidth={false}
      sx={{
        width: "100%",
        maxWidth: "100%",
        px: {
          xs: 1,
          sm: 2,
          md: 3,
        },
        boxSizing:
          "border-box",
      }}
    >
      {/* =================================================
          TOP CONTROLS
      ================================================= */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent:
            "space-between",
          gap: 2,
          mb: 1,
          p: 2,
          maxWidth: "100%",
          boxSizing:
            "border-box",
          flexWrap:
            "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems:
              "center",
            gap: 2,
            flexWrap:
              "wrap",
            maxWidth:
              "100%",
            flex: 1,
          }}
        >
          {/* SEARCH */}

          <TextField
            variant="outlined"
            placeholder="Search by Candidate Name"
            value={searchQuery}
            onChange={(e) =>
              setSearchQuery(
                e.target.value
              )
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color:
                        theme.palette
                          .primary
                          .main,
                      ml: 1.5,
                      fontSize: 22,
                    }}
                  />
                </InputAdornment>
              ),

              endAdornment:
                searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setSearchQuery(
                          ""
                        )
                      }
                      sx={{
                        color:
                          theme.palette
                            .text
                            .primary,
                        mr: 1,
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),

              sx: {
                borderRadius:
                  "30px",

                backgroundColor:
                  theme.palette
                    .mode ===
                  "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "#fff",

                boxShadow:
                  theme.palette
                    .mode ===
                  "dark"
                    ? "0 2px 8px rgba(0,0,0,0.3)"
                    : "0 2px 8px rgba(0,0,0,0.08)",

                "& .MuiOutlinedInput-notchedOutline":
                  {
                    border:
                      `1px solid ${
                        theme.palette
                          .mode ===
                        "dark"
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.12)"
                      }`,
                  },

                "&:hover": {
                  backgroundColor:
                    theme.palette
                      .mode ===
                    "dark"
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(245,245,245,1)",
                },

                "&.Mui-focused": {
                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      border:
                        `2px solid ${theme.palette.primary.main}`,
                    },
                },

                pl: 1.5,
                height: "50px",
              },
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 300,
              },

              maxWidth:
                "100%",

              "& .MuiInputBase-input":
                {
                  padding:
                    "10px 14px",

                  color:
                    theme.palette
                      .text
                      .primary,

                  fontWeight: 500,
                },
            }}
          />

          {/* ADD REGISTRATION */}

          <Button
            variant="contained"
            onClick={() =>
              navigate(
                "/add-registration"
              )
            }
            sx={{
              width: {
                xs: "100%",
                sm: 160,
              },

              minWidth: {
                xs: "100%",
                sm: 160,
              },

              maxWidth: {
                sm: 160,
              },

              borderRadius:
                "10px",

              height: "50px",

              fontWeight:
                "bold",

              backgroundColor:
                theme.palette
                  .primary
                  .main,

              "&:hover": {
                backgroundColor:
                  theme.palette
                    .primary
                    .dark,
              },
            }}
            startIcon={
              <AddIcon />
            }
          >
            ADD REGISTRATION
          </Button>

          {/* IMPORT ICON - same UI as Admission page */}
          <Tooltip title="Import Registrations" arrow placement="top">
            <IconButton
              onClick={() => {
                setImportFile(null);
                setShowImportModal(true);
              }}
              sx={{
                width: 50,
                height: 50,
                backgroundColor: theme.palette.warning.main,
                color: '#fff',
                '&:hover': { backgroundColor: theme.palette.warning.dark },
              }}
            >
              <UploadIcon />
            </IconButton>
          </Tooltip>

          {/* EXPORT ICON - same UI as Admission page */}
          <Tooltip title="Export Registrations" arrow placement="top">
            <IconButton
              onClick={() => setShowExportModal(true)}
              sx={{
                width: 50,
                height: 50,
                backgroundColor: theme.palette.info.main,
                color: '#fff',
                '&:hover': { backgroundColor: theme.palette.info.dark },
              }}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>

          {/* SHARE LINK */}

          <Tooltip
            title={
              selectedRegistration.registrationId !==
              null
                ? "Share selected registration"
                : "Select a registration from the table first"
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="success"
                sx={{
                  width: {
                    xs: "100%",
                    sm: 140,
                  },

                  minWidth: {
                    xs: "100%",
                    sm: 140,
                  },

                  borderRadius:
                    "10px",

                  height: "50px",

                  fontWeight:
                    "bold",
                }}
                startIcon={
                  <ShareIcon />
                }
                               onClick={
                  handleTopShare
                }
              >
                Share Link
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* USER GUIDE */}

        <Box
          sx={{
            display: "flex",
            alignItems:
              "center",
          }}
        >
          <IconButton
            onClick={() =>
              setShowUserGuideModal(
                true
              )
            }
            sx={{
              width: 60,
              height: 60,
              borderRadius:
                "50%",
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon
              sx={{
                fontSize: 30,
                color:
                  theme.palette
                    .primary
                    .main,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* =================================================
          SHARE MODAL
      ================================================= */}

      <Modal
        open={
          showShareModal
        }
        onClose={() =>
          setShowShareModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0,0,0,0.8)",
          },
        }}
      >
        <Box
          sx={{
            position:
              "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: {
              xs: "90%",
              sm: 500,
            },

            maxWidth:
              "calc(100% - 32px)",

            bgcolor:
              "background.paper",

            borderRadius: 2,
            boxShadow: 24,
            overflow:
              "hidden",
            p: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
            >
              Share Registration Link
            </Typography>

            <IconButton
              onClick={() =>
                setShowShareModal(
                  false
                )
              }
              sx={{
                p: 0.5,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            value={
              shareableLink
            }
            InputProps={{
              readOnly: true,
            }}
            sx={{
              mb: 2,
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent:
                "flex-end",
              gap: 2,
              flexWrap:
                "wrap",
            }}
          >
            <Button
              variant="contained"
              onClick={
                handleCopyLink
              }
            >
              Copy Link
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={
                <WhatsAppIcon />
              }
              onClick={() => {
                if (
                  !shareableLink
                ) {
                  return;
                }

                window.open(
                  `https://wa.me/?text=${encodeURIComponent(
                    shareableLink
                  )}`,
                  "_blank"
                );
              }}
            >
              Share to WhatsApp
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* =================================================
          REGISTRATION TABLE
      ================================================= */}

      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflow:
            "hidden",
          boxSizing:
            "border-box",

          backgroundColor:
            theme.palette
              .background
              .paper,

          borderRadius:
            "16px",
        }}
      >
        <CustomTableScrollbar>
          <TableContainer
            sx={{
              width: "100%",
              maxWidth: "100%",
              overflow:
                "visible",
              borderRadius:
                "16px",
            }}
          >
            <Table
              sx={{
                minWidth: 1050,
                width: "100%",
                tableLayout:
                  "fixed",

                "& .MuiTableCell-root":
                  {
                    wordBreak:
                      "break-word",

                    overflowWrap:
                      "anywhere",

                    whiteSpace:
                      "normal",

                    verticalAlign:
                      "middle",

                    minWidth: 0,
                  },

                "& .MuiTableHead-root .MuiTableCell-root":
                  {
                    fontWeight:
                      "bold",

                    backgroundColor:
                      theme.palette
                        .primary
                        .main,

                    color:
                      theme.palette
                        .primary
                        .contrastText,

                    wordBreak:
                      "break-word",

                    overflowWrap:
                      "anywhere",
                  },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: "7%",
                      fontSize:
                        "0.95rem",
                    }}
                  >
                    S.No
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "15%",
                      fontSize:
                        "0.95rem",
                    }}
                  >
                    Registration No.
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "18%",
                      fontSize:
                        "0.95rem",
                    }}
                  >
                    Candidate Name
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "9%",
                      fontSize:
                        "0.95rem",
                    }}
                  >
                    Gender
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "19%",
                      fontSize:
                        "0.95rem",
                    }}
                  >
                    Email Id
                  </TableCell>

                  <TableCell
                    sx={{
                      width: "13%",
                      fontSize:
                        "0.95rem",
                    }}
                  >
                    Mobile No.
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      width: "19%",
                      fontWeight:
                        "bold",

                      backgroundColor:
                        theme.palette
                          .primary
                          .dark,

                      color:
                        theme.palette
                          .primary
                          .contrastText,

                      fontSize:
                        "0.95rem",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{
                        py: 8,
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="text.primary"
                      >
                        Loading registrations...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : fetchError ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{
                        py: 8,
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="error"
                      >
                        {fetchError}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length >
                  0 ? (
                  paginatedData.map(
                    (
                      registration,
                      index
                    ) => {
                      const isSelected =
                        selectedRegistration.registrationId ===
                        registration.registrationId;

                      return (
                        <TableRow
                          key={
                            registration.registrationId
                          }
                          onClick={() =>
                            handleSelectRegistration(
                              registration
                            )
                          }
                          sx={{
                            cursor:
                              "pointer",

                            backgroundColor:
                              isSelected
                                ? theme
                                    .palette
                                    .action
                                    .selected
                                : index %
                                      2 ===
                                    0
                                  ? theme
                                      .palette
                                      .action
                                      .hover
                                  : "inherit",

                            "&:hover":
                              {
                                backgroundColor:
                                  theme
                                    .palette
                                    .action
                                    .selected,
                              },
                          }}
                        >
                          <TableCell>
                            {(page - 1) *
                              rowsPerPage +
                              index +
                              1}
                          </TableCell>

                          <TableCell>
                            {registration.registrationCode ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {registration.candidateName ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {registration.gender ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {registration.email ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {registration.mobileNo ||
                              "-"}
                          </TableCell>

                          <TableCell
                            align="center"
                            onClick={(e) =>
                              e.stopPropagation()
                            }
                            sx={{
                              whiteSpace:
                                "nowrap",
                            }}
                          >

                            {/* PRINT */}

                            <Tooltip
                              title="View / Print"
                              arrow
                            >
                              <IconButton
                                onClick={() =>
                                  handleView(
                                    registration
                                  )
                                }
                                color="primary"
                                size="small"
                                sx={{
                                  borderRadius:
                                    1.5,

                                  transition:
                                    "all 0.2s ease",

                                  "&:hover":
                                    {
                                      backgroundColor:
                                        "rgba(25, 118, 210, 0.10)",
                                    },
                                }}
                              >
                                <PrintIcon
                                  sx={{
                                    fontSize: 19,
                                  }}
                                />
                              </IconButton>
                            </Tooltip>

                            {/* EDIT */}

                            <Tooltip
                              title="Edit Registration"
                              arrow
                            >
                              <IconButton
                                onClick={() =>
                                  handleEdit(
                                    registration
                                  )
                                }
                                color="warning"
                                size="small"
                                sx={{
                                  borderRadius:
                                    1.5,

                                  transition:
                                    "all 0.2s ease",

                                  "&:hover":
                                    {
                                      backgroundColor:
                                        "rgba(237, 108, 2, 0.10)",
                                    },
                                }}
                              >
                                <EditIcon
                                  sx={{
                                    fontSize: 19,
                                  }}
                                />
                              </IconButton>
                            </Tooltip>

                            {/* DELETE */}

                            <Tooltip
                              title="Delete Registration"
                              arrow
                            >
                              <IconButton
                                color="error"
                                onClick={() =>
                                  handleShowDeleteModal(
                                    registration.registrationId,
                                    registration.candidateName
                                  )
                                }
                                size="small"
                                sx={{
                                  borderRadius:
                                    1.5,

                                  transition:
                                    "all 0.2s ease",

                                  "&:hover":
                                    {
                                      backgroundColor:
                                        "rgba(211, 47, 47, 0.10)",
                                    },
                                }}
                              >
                                <DeleteIcon
                                  sx={{
                                    fontSize: 19,
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{
                        py: 8,
                      }}
                    >
                      <Typography
                        variant="h6"
                        color="text.primary"
                      >
                        No registrations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {/* =================================================
          PAGINATION
      ================================================= */}

      {filteredData.length >
        0 && (
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            mt: 1,
            p: 2,
            boxSizing:
              "border-box",
            gap: 2,
            flexWrap:
              "wrap",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color:
                theme.palette
                  .primary
                  .main,
            }}
          >
            Page{" "}
            <strong>
              {page}
            </strong>{" "}
            of{" "}
            <strong>
              {totalPages}
            </strong>
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flexWrap:
                "wrap",
            }}
          >
            <IconButton
              onClick={() =>
                handleChangePage(
                  null,
                  page - 1
                )
              }
              disabled={
                page === 1
              }
              sx={{
                color:
                  theme.palette
                    .primary
                    .main,

                "&:hover": {
                  backgroundColor:
                    theme.palette
                      .action
                      .hover,
                },

                "&.Mui-disabled":
                  {
                    opacity:
                      0.3,
                  },

                borderRadius:
                  "50%",

                width: 36,
                height: 36,
              }}
            >
              <ChevronLeft />
            </IconButton>

            {[
              ...Array(
                totalPages
              ),
            ].map(
              (_, index) => (
                <IconButton
                  key={
                    index + 1
                  }
                  onClick={() =>
                    handleChangePage(
                      null,
                      index + 1
                    )
                  }
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius:
                      "50%",

                    backgroundColor:
                      page ===
                      index + 1
                        ? theme
                            .palette
                            .primary
                            .main
                        : "transparent",

                    color:
                      page ===
                      index + 1
                        ? theme
                            .palette
                            .primary
                            .contrastText
                        : theme
                            .palette
                            .text
                            .primary,

                    "&:hover":
                      {
                        backgroundColor:
                          page ===
                          index + 1
                            ? theme
                                .palette
                                .primary
                                .dark
                            : theme
                                .palette
                                .action
                                .hover,
                      },
                  }}
                >
                  {index + 1}
                </IconButton>
              )
            )}

            <IconButton
              onClick={() =>
                handleChangePage(
                  null,
                  page + 1
                )
              }
              disabled={
                page >=
                totalPages
              }
              sx={{
                color:
                  theme.palette
                    .primary
                    .main,

                "&:hover": {
                  backgroundColor:
                    theme.palette
                      .action
                      .hover,
                },

                "&.Mui-disabled":
                  {
                    opacity:
                      0.3,
                  },

                borderRadius:
                  "50%",

                width: 36,
                height: 36,
              }}
            >
              <ChevronRight />
            </IconButton>

            <FormControl
              size="small"
              sx={{
                minWidth: 100,
                ml: 1,
              }}
            >
              <InputLabel
                id="rows-per-page-label"
                sx={{
                  color:
                    theme.palette
                      .text
                      .primary,

                  "&.Mui-focused": {
                    color:
                      theme.palette
                        .primary
                        .main,
                  },
                }}
              >
                Rows
              </InputLabel>

              <Select
                labelId="rows-per-page-label"
                value={
                  rowsPerPage
                }
                onChange={
                  handleChangeRowsPerPage
                }
                label="Rows"
                sx={{
                  height: 36,

                  color:
                    theme.palette
                      .text
                      .primary,

                  "& .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme.palette
                          .text
                          .primary,
                    },

                  "&:hover .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme.palette
                          .primary
                          .main,
                    },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor:
                        theme.palette
                          .primary
                          .main,
                    },
                }}
              >
                {rowsPerPageOptions.map(
                  (option) => (
                    <MenuItem
                      key={
                        option
                      }
                      value={
                        option
                      }
                    >
                      {option}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      {/* =================================================
          PDF COLOR PICKER MODAL
      ================================================= */}

      <Modal
        open={
          showPdfColorModal
        }
        onClose={() => {
          if (!pdfLoading) {
            setShowPdfColorModal(
              false
            );
          }
        }}
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0,0,0,0.8)",
          },
        }}
      >
        <Box
          sx={{
            position:
              "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: {
              xs: "92%",
              sm: 460,
            },

            maxWidth:
              "calc(100% - 32px)",

            bgcolor:
              "background.paper",

            borderRadius: 3,
            boxShadow: 24,
            overflow:
              "hidden",
          }}
        >
          {/* MODAL HEADER */}

          <Box
            sx={{
              backgroundColor:
                pdfColorPrimary,

              px: 2.5,
              py: 1.8,

              display: "flex",
              alignItems:
                "center",
              justifyContent:
                "space-between",

              transition:
                "background-color 0.2s ease",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                }}
              >
                PDF Appearance
              </Typography>
            </Box>

            <IconButton
              onClick={() =>
                setShowPdfColorModal(
                  false
                )
              }
              disabled={
                pdfLoading
              }
              sx={{
                color: "#fff",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* CONTENT */}

          <Box
            sx={{
              p: 3,
            }}
          >
            {/* PRIMARY COLOR */}

            <Box
              sx={{
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "space-between",
                gap: 2,
                mb: 2.5,
              }}
            >
              <Box>
                <Typography
                  fontWeight={600}
                >
                  PDF Background Color
                </Typography>

                <Typography
                  variant="caption"
                  color="text.primary"
                >
                  From Global Sidebar settings
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 1,
                }}
              >
                <Box
                  component="input"
                  type="color"
                  value={
                    pdfColorPrimary
                  }
                  onChange={(e) =>
                    setPdfColorPrimary(
                      e.target.value
                    )
                  }
                  disabled={
                    pdfLoading
                  }
                  sx={{
                    width: 48,
                    height: 40,
                    p: 0.5,

                    border:
                      "1px solid",

                    borderColor:
                      "divider",

                    borderRadius:
                      1.5,

                    cursor:
                      "pointer",

                    backgroundColor:
                      "transparent",
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    fontFamily:
                      "monospace",
                    minWidth: 75,
                  }}
                >
                  {pdfColorPrimary.toUpperCase()}
                </Typography>
              </Box>
            </Box>

            {/* PDF TEXT COLOR */}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 2.5,
              }}
            >
              <Box>
                <Typography fontWeight={600}>
                  PDF Text Color
                </Typography>

                <Typography variant="caption" color="text.primary">
                  From Global Sidebar settings
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  component="input"
                  type="color"
                  value={pdfColorText}
                  onChange={(e) =>
                    setPdfColorText(e.target.value)
                  }
                  disabled={pdfLoading}
                  sx={{
                    width: 48,
                    height: 40,
                    p: 0.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    cursor: "pointer",
                    backgroundColor: "transparent",
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    minWidth: 75,
                  }}
                >
                  {pdfColorText.toUpperCase()}
                </Typography>
              </Box>
            </Box>

            {/* ACCENT COLOR */}

            <Box
              sx={{
                display: "flex",
                alignItems:
                  "center",
                justifyContent:
                  "space-between",
                gap: 2,
                mb: 3,
              }}
            >
              <Box>
                <Typography
                  fontWeight={600}
                >
                  Accent Color
                </Typography>

                <Typography
                  variant="caption"
                  color="text.primary"
                >
                  Section headings
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 1,
                }}
              >
                <Box
                  component="input"
                  type="color"
                  value={
                    pdfColorAccent
                  }
                  onChange={(e) =>
                    setPdfColorAccent(
                      e.target.value
                    )
                  }
                  disabled={
                    pdfLoading
                  }
                  sx={{
                    width: 48,
                    height: 40,
                    p: 0.5,

                    border:
                      "1px solid",

                    borderColor:
                      "divider",

                    borderRadius:
                      1.5,

                    cursor:
                      "pointer",

                    backgroundColor:
                      "transparent",
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    fontFamily:
                      "monospace",
                    minWidth: 75,
                  }}
                >
                  {pdfColorAccent.toUpperCase()}
                </Typography>
              </Box>
            </Box>

            {/* PREVIEW */}

            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                fontWeight: 700,
              }}
            >
              Preview
            </Typography>

            <Box
              sx={{
                border:
                  "1px solid",
                borderColor:
                  "divider",
                borderRadius: 2,
                overflow:
                  "hidden",
                mb: 3,
              }}
            >
              {/* PDF HEADER PREVIEW */}

              <Box
                sx={{
                  height: 58,
                  backgroundColor:
                    pdfColorPrimary,

                  display: "flex",
                  alignItems:
                    "center",

                  px: 2,

                  transition:
                    "background-color 0.2s ease",
                }}
              >
                {/* DYNAMIC LOGO PREVIEW */}

                {branchLogo ? (
                  <Box
                    component="img"
                    src={
                      branchLogo
                    }
                    alt="Branch Logo"
                    sx={{
                      width: 36,
                      height: 36,
                      objectFit:
                        "contain",
                      backgroundColor:
                        "#fff",
                      borderRadius: 1,
                      p: 0.3,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1,
                      backgroundColor:
                        "#fff",
                      display: "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      fontSize: 8,
                      fontWeight: 700,
                      color:
                        pdfColorPrimary,
                    }}
                  >
                    LOGO
                  </Box>
                )}

                <Box
                  sx={{
                    ml: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      fontSize:
                        "0.9rem",
                    }}
                  >
                    {branchName ||
                      "Organization Name"}
                  </Typography>

                  <Typography
                    sx={{
                      color:
                        "rgba(255,255,255,0.8)",
                      fontSize:
                        "0.7rem",
                    }}
                  >
                    Registration Confirmation
                  </Typography>
                </Box>
              </Box>

              {/* SECTION PREVIEW */}

              <Box
                sx={{
                  height: 30,
                  backgroundColor:
                    pdfColorAccent,

                  display: "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",

                  transition:
                    "background-color 0.2s ease",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ color: pdfColorText }}
                >
                  Personal Details
                </Typography>
              </Box>

              {/* CONTENT PREVIEW */}

              <Box
                sx={{
                  p: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: "75%",
                    height: 7,
                    borderRadius: 1,
                    backgroundColor:
                      "divider",
                    mb: 1,
                  }}
                />

                <Box
                  sx={{
                    width: "55%",
                    height: 7,
                    borderRadius: 1,
                    backgroundColor:
                      "divider",
                    mb: 1,
                  }}
                />

                <Box
                  sx={{
                    width: "65%",
                    height: 7,
                    borderRadius: 1,
                    backgroundColor:
                      "divider",
                  }}
                />
              </Box>
            </Box>

            {/* ACTIONS */}

            <Box
              sx={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "center",
                gap: 1.5,
              }}
            >
              <Button
                variant="text"
                onClick={
                  handleResetPdfColors
                }
                disabled={
                  pdfLoading
                }
              >
                Reset Colors
              </Button>

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() =>
                    setShowPdfColorModal(
                      false
                    )
                  }
                  disabled={
                    pdfLoading
                  }
                 data-app-cancel="true">
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  startIcon={
                    <PrintIcon />
                  }
                  onClick={
                    handleGeneratePDF
                  }
                  disabled={
                    pdfLoading
                  }
                >
                  {pdfLoading
                    ? "Generating..."
                    : "Generate PDF"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ======================================================
          IMPORT MODAL - Admission page layout
      ====================================================== */}
      <Modal
        open={showImportModal}
        onClose={() => {
          if (!importing) {
            setShowImportModal(false);
            setImportFile(null);
          }
        }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}
      >
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', width: 480, maxWidth: '90vw',
          bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24,
          overflow: 'hidden', maxHeight: '90vh', outline: 'none',
        }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>Import Registrations</Typography>
            <IconButton onClick={() => { setShowImportModal(false); setImportFile(null); }} disabled={importing} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px', overflowY: 'auto', maxHeight: 'calc(90vh - 60px)' }}>
            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Step 1: Download the template</Typography>
            <Button variant="outlined" startIcon={downloadingTemplate ? <CircularProgress size={18} /> : <DownloadIcon />} onClick={handleDownloadRegistrationTemplate} disabled={downloadingTemplate} sx={{ mb: 3 }}>
              {downloadingTemplate ? 'Downloading...' : 'Download Template'}
            </Button>

            <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>Step 2: Enter data in the proper format</Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>Fill in the registration details in the downloaded Excel file. Keep the template column headings unchanged. Related fields should contain IDs.</Typography>

            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Step 3: Choose the Excel file</Typography>
            <Button variant="contained" component="label" sx={{ mb: 1 }} disabled={importing}>
              Select File
              <input type="file" hidden accept=".xlsx,.xls" onChange={handleRegistrationFileChange} onClick={(event) => { event.target.value = null; }} />
            </Button>
            {importFile && <Typography variant="body2" sx={{ mb: 1, overflowWrap: 'anywhere' }}>Selected: {importFile.name}</Typography>}

            <Typography sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Step 4: Click Import</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button variant="outlined" color="error" onClick={() => { setShowImportModal(false); setImportFile(null); }} disabled={importing} data-app-cancel="true">Cancel</Button>
              <Button variant="contained" onClick={handleRegistrationImport} disabled={!importFile || importing} startIcon={importing ? <CircularProgress size={18} color="inherit" /> : null}>
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ======================================================
          EXPORT MODAL - Admission page layout
      ====================================================== */}
      <Modal
        open={showExportModal}
        onClose={() => { if (!exporting) setShowExportModal(false); }}
        BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.8)' } }}
      >
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', width: 400, maxWidth: '90vw',
          bgcolor: 'background.paper', borderRadius: 2, boxShadow: 24, overflow: 'hidden', outline: 'none',
        }}>
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>Export Registrations</Typography>
            <IconButton onClick={() => setShowExportModal(false)} disabled={exporting} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography sx={{ mb: 1, fontWeight: 'bold' }}>Which registrations do you want to export?</Typography>
            <RadioGroup value={exportType} onChange={(event) => setExportType(event.target.value)}>
              <FormControlLabel value="active" control={<Radio />} label="Active records only" />
              <FormControlLabel value="all" control={<Radio />} label="All records (active + inactive)" />
            </RadioGroup>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.primary' }}>The current organization, selected branch, and search filters will be applied.</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button variant="outlined" color="error" onClick={() => setShowExportModal(false)} disabled={exporting} data-app-cancel="true">Cancel</Button>
              <Button variant="contained" onClick={handleRegistrationExport} disabled={exporting} startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}>
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
        anchorOrigin={{
          vertical: "top",
          horizontal:
            "center",
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(
              false
            )
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: "100%",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* =================================================
          DELETE MODAL
      ================================================= */}

      <Modal
        open={
          showDeleteModal
        }
        onClose={() =>
          setShowDeleteModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0,0,0,0.8)",
          },
        }}
      >
        <Box
          sx={{
            position:
              "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: {
              xs: "90%",
              sm: 400,
            },

            bgcolor:
              "background.paper",

            borderRadius: 2,
            boxShadow: 24,
            overflow:
              "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary
                  .main,

              padding:
                "12px 16px",
            }}
          >
            <Typography
              id="delete-modal-title"
              variant="h6"
              sx={{
                color: "#fff",
              }}
            >
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          <Box
            sx={{
              padding: 2.5,
            }}
          >
            <Typography
              id="delete-modal-description"
              sx={{
                mb: 2,
              }}
            >
              Are you sure you want to
              delete{" "}
              <strong>
                {
                  selectedRegistration.candidateName
                }
              </strong>{" "}
              registration?
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent:
                  "flex-end",
                mt: 3,
              }}
            >
              <Button
                onClick={() =>
                  setShowDeleteModal(
                    false
                  )
                }
                variant="outlined"
                sx={{
                  mr: 1,
                }}
                disabled={
                  deleteLoading
                }
               data-app-cancel="true">
                Cancel
              </Button>

              <Button
                variant="contained"
                color="error"
                onClick={
                  handleDelete
                }
                disabled={
                  deleteLoading
                }
              >
                {deleteLoading
                  ? "Deleting..."
                  : "Yes, Delete"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =================================================
          SUCCESS MODAL
      ================================================= */}

      <Modal
        open={
          showSuccessModal
        }
        onClose={() =>
          setShowSuccessModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0,0,0,0.8)",
          },
        }}
      >
        <Box
          sx={{
            position:
              "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: {
              xs: "90%",
              sm: 400,
            },

            bgcolor:
              "background.paper",

            borderRadius: 2,
            boxShadow: 24,
            overflow:
              "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary
                  .main,

              padding:
                "12px 16px",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#fff",
              }}
            >
              Registration Updated ✅
            </Typography>
          </Box>

          <Box
            sx={{
              padding: 2.5,
            }}
          >
            <Typography
              sx={{
                mb: 2,
              }}
            >
              Registration Updated Successfully
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent:
                  "flex-end",
                mt: 3,
              }}
            >
              <Button
                onClick={() =>
                  setShowSuccessModal(
                    false
                  )
                }
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* =================================================
          USER GUIDE
      ================================================= */}

      <Modal
        open={
          showUserGuideModal
        }
        onClose={() =>
          setShowUserGuideModal(
            false
          )
        }
        BackdropProps={{
          style: {
            backgroundColor:
              "rgba(0,0,0,0.8)",
          },
        }}
      >
        <Box
          sx={{
            position:
              "absolute",
            top: "50%",
            left: "50%",
            transform:
              "translate(-50%, -50%)",

            width: {
              xs: "92%",
              sm: 800,
            },

            maxWidth:
              "calc(100% - 32px)",

            bgcolor:
              "background.paper",

            borderRadius: 2,
            boxShadow: 24,
            overflow:
              "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary
                  .main,

              padding:
                "12px 16px",

              display: "flex",
              justifyContent:
                "space-between",
              alignItems:
                "center",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: "#fff",
                fontWeight:
                  "bold",

                fontSize: {
                  xs: "1.5rem",
                  sm: "2.125rem",
                },
              }}
            >
              Registration Module - User Guide
            </Typography>

            <IconButton
              onClick={() =>
                setShowUserGuideModal(
                  false
                )
              }
              sx={{
                color: "#fff",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar
            sx={{
              maxHeight:
                "70vh",

              overflowY:
                "auto",

              overflowX:
                "auto",

              padding: 2.5,

              boxSizing:
                "border-box",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight:
                  "bold",

                color:
                  theme.palette
                    .primary
                    .main,

                mb: 1,
              }}
            >
              ➤ Follow these steps to add a new registration:
            </Typography>

            <ul
              style={{
                marginLeft:
                  "20px",

                lineHeight:
                  "1.8",
              }}
            >
              <li>
                Click the{" "}
                <strong>
                  Add Registration
                </strong>{" "}
                button to open the registration form.
              </li>

              <li>
                Fill in all required student details: registration code, candidate name, gender, mobile number, email, and address.
              </li>

              <li>
                Select options for gender, country, state, city, nationality, and course from the dropdowns.
              </li>

              <li>
                Review all entered information for accuracy.
              </li>

              <li>
                Click{" "}
                <strong>
                  Save
                </strong>{" "}
                to save the registration details.
              </li>

              <li>
                The new registration will appear in the Registration List.
              </li>

              <li>
                Use the search bar to quickly find registration records by candidate name.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,

                color:
                  theme.palette
                    .text
                    .primary,
              }}
            >
              ⚠️ Note: Ensure all mandatory fields are filled to avoid submion errors.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontWeight:
                  "bold",

                color:
                  theme.palette
                    .primary
                    .main,

                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to edit a registration:
            </Typography>

            <ul
              style={{
                marginLeft:
                  "20px",

                lineHeight:
                  "1.8",
              }}
            >
              <li>
                Go to the{" "}
                <strong>
                  Registration Page
                </strong>.
              </li>

              <li>
                Find the student you want to update in the Registration List.
              </li>

              <li>
                Click the{" "}
                <strong>
                  Edit
                </strong>{" "}
                pencil icon.
              </li>

              <li>
                Update the necessary information in the form.
              </li>

              <li>
                Click{" "}
                <strong>
                  Update
                </strong>{" "}
                to save changes.
              </li>

              <li>
                Double-check all updates before saving.
              </li>
            </ul>

            <Typography
              variant="body1"
              sx={{
                fontWeight:
                  "bold",

                color:
                  theme.palette
                    .primary
                    .main,

                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to generate a registration PDF:
            </Typography>

            <ul
              style={{
                marginLeft:
                  "20px",

                lineHeight:
                  "1.8",
              }}
            >
              <li>
                Go to the{" "}
                <strong>
                  Registration Table
                </strong>.
              </li>

              <li>
                In the Action column, click the{" "}
                <strong>
                  Print
                </strong>{" "}
                printer icon.
              </li>

              <li>
                Choose your preferred PDF colors.
              </li>

              <li>
                Click{" "}
                <strong>
                  Generate PDF
                </strong>.
              </li>

              <li>
                The registration form PDF will generate and open in a new tab.
              </li>
            </ul>

            <Typography
              variant="body1"
              sx={{
                fontWeight:
                  "bold",

                color:
                  theme.palette
                    .primary
                    .main,

                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to share a registration link:
            </Typography>

            <ul
              style={{
                marginLeft:
                  "20px",

                lineHeight:
                  "1.8",
              }}
            >
              <li>
                Select a registration from the Registration Table.
              </li>

              <li>
                Click the{" "}
                <strong>
                  Share
                </strong>{" "}
                icon in the Action column.
              </li>

              <li>
                Alternatively, after selecting a registration, click the{" "}
                <strong>
                  Share Link
                </strong>{" "}
                button at the top.
              </li>

              <li>
                A modal will open with options to{" "}
                <strong>
                  Copy Link
                </strong>{" "}
                or{" "}
                <strong>
                  Share to WhatsApp
                </strong>.
              </li>

              <li>
                Click{" "}
                <strong>
                  Copy Link
                </strong>{" "}
                to copy the link or{" "}
                <strong>
                  Share to WhatsApp
                </strong>{" "}
                to share directly.
              </li>

              <li>
                Paste the link in a browser to open the registration form.
              </li>

              <li>
                Fill in all required details.
              </li>

              <li>
                Click{" "}
                <strong>
                  Submit
                </strong>{" "}
                to complete the form.
              </li>

              <li>
                After submion, you'll see a "Registration Successful" confirmation.
              </li>
            </ul>

            <Typography
              variant="body1"
              sx={{
                fontWeight:
                  "bold",

                color:
                  theme.palette
                    .primary
                    .main,

                mt: 3,
                mb: 1,
              }}
            >
              ➤ Follow these steps to delete a registration:
            </Typography>

            <ul
              style={{
                marginLeft:
                  "20px",

                lineHeight:
                  "1.8",
              }}
            >
              <li>
                Go to the{" "}
                <strong>
                  Registration Table
                </strong>.
              </li>

              <li>
                Find the student whose registration you want to delete.
              </li>

              <li>
                Click the{" "}
                <strong>
                  Delete
                </strong>{" "}
                trash icon.
              </li>

              <li>
                Confirm deletion in the modal.
              </li>

              <li>
                The registration will be removed from the list.
              </li>
            </ul>

            <Typography
              variant="body2"
              sx={{
                mt: 2,

                color:
                  theme.palette
                    .text
                    .primary,
              }}
            >
              ⚠️ Note: Deleted registrations cannot be recovered.
            </Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>
    </Container>
  );
};

export default Registration;
